// Components/PaymentMethodForm/PaymentMethodForm.jsx
import * as React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Info } from 'lucide-react';

import { paymentMethodSchema, getDefaultValues } from './paymentMethodSchema';
import { insuranceCompanies as insuranceCompaniesConstant } from '@/constants/InsuranceCompanies';
import { useCartData } from '../../context/CartDataContext';
import { useRegistration } from '../../context/RegistrationContext';
import { useSettingsState } from '@/components/context/SettingsContext';
import { scrollToFirstError } from '@/v2/utils/formUtils';

export default function PaymentMethodForm({ onChange, onValid, onInvalid }) {
  const { selectedTimeSlot, service } = useCartData();
  const shouldHideEBank = !service?.canBePaidByPaysera;
  const form = useForm({
    resolver: zodResolver(paymentMethodSchema),
    mode: 'onSubmit',
    shouldFocusError: false,
    defaultValues: getDefaultValues(shouldHideEBank),
  });
  // ⬇️ include getValues so we can preserve current fields on reset
  const { reset, watch, setValue, getValues } = form;
  const paymentId = watch('paymentId');
  const insurance = watch('insurance');
  const { agreements } = useSettingsState();
  const { paymentInfo, setPaymentInfo } = useRegistration();

  const lockPaymentSection =
    typeof paymentInfo?.computedTotal === 'number'
      ? paymentInfo.computedTotal === 0
      : false;

  // ✅ LIVE-UPDATE ONLY THE INSURANCE FIELD IN CONTEXT
  React.useEffect(() => {
    if (insurance !== undefined) {
      setPaymentInfo((prev) => ({ ...(prev || {}), insurance }));
    }
  }, [insurance, setPaymentInfo]);

  // ✅ RESET effect that PRESERVES current values if paymentInfo doesn't have them,
  // preventing the radio from jumping back to defaults like 'EBank'
  React.useEffect(() => {
    if (!paymentInfo) return;

    const defaults = getDefaultValues(shouldHideEBank);
    const current = getValues();

    reset({
      ...defaults,
      ...paymentInfo,
      // preserve current selection if paymentInfo doesn't specify it
      paymentId:
        paymentInfo.paymentId ?? current.paymentId ?? defaults.paymentId,
      insurance:
        paymentInfo.insurance ?? current.insurance ?? defaults.insurance,
      company: paymentInfo.company ?? current.company ?? defaults.company,
      agreePrivacy:
        paymentInfo.agreePrivacy ??
        current.agreePrivacy ??
        defaults.agreePrivacy,
      agreePortal:
        paymentInfo.agreePortal ?? current.agreePortal ?? defaults.agreePortal,
    });
  }, [paymentInfo, reset, getValues]);

  const [insuranceOptions, setInsuranceOptions] = React.useState(
    insuranceCompaniesConstant,
  );

  React.useEffect(() => {
    const shouldFilter =
      Boolean(paymentInfo?.discountCodeNumber) ||
      Boolean(selectedTimeSlot?.isExtra);

    if (shouldFilter) {
      setInsuranceOptions(
        insuranceCompaniesConstant.filter((c) => c.value !== 'Compensa'),
      );
    } else {
      setInsuranceOptions(insuranceCompaniesConstant);
    }
  }, [paymentInfo?.discountCodeNumber, selectedTimeSlot?.isExtra]);

  const shouldDisplayJuridicalOption = React.useCallback(() => {
    return !paymentInfo?.discountCodeNumber;
  }, [paymentInfo?.discountCodeNumber]);

  const shouldDisplayOtherOptions = () => {
    return !paymentInfo?.couponIds?.length > 0;
  };

  React.useEffect(() => {
    if (paymentId === 'Juridical' && !shouldDisplayJuridicalOption()) {
      setValue('paymentId', shouldHideEBank ? '' : 'EBank', {
        shouldDirty: true,
      });
    }
  }, [paymentId, shouldDisplayJuridicalOption, shouldHideEBank, setValue]);

  const values = watch();
  React.useEffect(() => {
    onChange?.({
      paymentId: values.paymentId,
      insurance: values.paymentId === 'Insurance' ? values.insurance : '',
      company: values.paymentId === 'Juridical' ? values.company : '',
      agreements: { privacy: values.agreePrivacy, portal: values.agreePortal },
    });
  }, [values, onChange]);

  const options = [
    {
      id: 'EBank',
      label: 'Elektroninė bankininkystė',
      info:
        'Pasirinkus šį apmokėjimo būdą, būsite automatiškai nukreipti į Paysera sistemą, ' +
        'kur galėsite apmokėti per pasirinkto banko internetinę bankininkystę.',
    },
    {
      id: 'Insurance',
      label: 'Apmokėjimas privačiu draudimu',
      info:
        'Pasirinkus šį apmokėjimo būdą patvirtinate ir garantuojate, kad turite ir ' +
        'asmens sveikatos priežiūros paslaugų teikimo metu turėsite galiojantį ' +
        'savanoriškąjį (papildomą) sveikatos draudimą su pakankamu draudimo sumos likučiu. ' +
        'Jei draudimo bendrovė neapmokės už suteiktas paslaugas arba sumokėtas sumas pareikalaus ' +
        'grąžinti, už suteiktas paslaugas prašysime apmokėti per 5 (penkias) kalendorines dienas ' +
        'nuo mokėjimo pranešimo išsiuntimo dienos.',
      extra: 'insurance',
    },
    {
      id: 'Juridical',
      label: 'Mokės juridinis mokėtojas',
      info:
        'Pasirinkus šį apmokėjimo būdą patvirtinate ir garantuojate, kad nurodytas juridinis ' +
        'mokėtojas apmokės už pasirinktą paslaugą. Jei juridinis mokėtojas neapmokės už suteiktas ' +
        'paslaugas arba sumokėtas sumas pareikalaus grąžinti, už suteiktas paslaugas prašysime ' +
        'apmokėti per 5 (penkias) kalendorines dienas nuo mokėjimo pranešimo išsiuntimo dienos.',
      extra: 'corp',
    },
    {
      id: 'OnSite',
      label: 'Mokėjimas atvykus į padalinį',
      info: 'Pasirinkus šį mokėjimo būdą patvirtinate, kad už paslaugas atsiskaitysite Affidea padalinyje.',
    },
  ];

  const handleChange = (val) => {
    if (lockPaymentSection) return;
    setValue('paymentId', val, { shouldDirty: true });
    if (val !== 'Insurance') setValue('insurance', '', { shouldDirty: true });
    if (val !== 'Juridical') setValue('company', '', { shouldDirty: true });
  };

  return (
    <FormProvider {...form}>
      <Form {...form}>
        <form
          id="payment-method-form"
          className="w-full space-y-6"
          noValidate
          onSubmit={form.handleSubmit(
            (vals) => {
              setPaymentInfo((prev) => ({ ...(prev || {}), ...vals }));
              onValid?.(vals);
            },
            (errors) => {
              scrollToFirstError(errors); // ✅ Scroll to first error
              onInvalid?.(errors);
            },
          )}
        >
          {/* Mokėjimo būdas */}
          <section className="w-full space-y-3">
            <FormField
              control={form.control}
              name="paymentId"
              render={({ field }) =>
                !lockPaymentSection && (
                  <FormItem data-error-anchor={field.name}>
                    <FormLabel>Mokėjimo būdas</FormLabel>
                    <div
                      className={`w-full rounded-lg border overflow-hidden ${
                        lockPaymentSection ? 'opacity-60' : ''
                      }`}
                    >
                      <FormControl>
                        <RadioGroup
                          value={field.value}
                          onValueChange={handleChange}
                          className={`grid w-full gap-0 divide-y ${
                            lockPaymentSection
                              ? 'pointer-events-none select-none'
                              : ''
                          }`}
                        >
                          {options
                            .filter((opt) => {
                              if (opt.id === 'EBank' && shouldHideEBank) {
                                return false;
                              }
                              if (
                                !shouldDisplayOtherOptions() &&
                                !service.hasPriceFromConfigured
                              ) {
                                return opt.id === 'EBank';
                              } else if (service?.hasPriceFromConfigured) {
                                return opt.id === 'OnSite';
                              }
                              return true;
                            })
                            .map((opt) => {
                              if (
                                (opt.id === 'Juridical' &&
                                  !shouldDisplayJuridicalOption()) ||
                                (service?.hasPriceFromConfigured &&
                                  opt.id !== 'OnSite')
                              ) {
                                return null;
                              }

                              const inputId = `payment-${opt.id}`;
                              const selected = field.value === opt.id;
                              const showExtraControls =
                                selected &&
                                (opt.extra === 'insurance' ||
                                  opt.extra === 'corp');

                              return (
                                <div key={opt.id} className="w-full px-4 py-0">
                                  <label
                                    htmlFor={inputId}
                                    className={`min-h-12 flex items-center justify-between ${
                                      lockPaymentSection
                                        ? 'cursor-default'
                                        : 'cursor-pointer'
                                    }`}
                                  >
                                    <div className="flex items-center gap-3 min-w-0">
                                      <RadioGroupItem
                                        id={inputId}
                                        value={opt.id}
                                        disabled={lockPaymentSection}
                                      />
                                      <span className="text-sm font-medium">
                                        {opt.label}
                                      </span>
                                    </div>
                                  </label>

                                  {showExtraControls && (
                                    <div className="-mx-4 bg-background px-4 pt-2 pb-4">
                                      {opt.extra === 'insurance' && (
                                        <FormField
                                          control={form.control}
                                          name="insurance"
                                          render={({
                                            field: insuranceField,
                                          }) => (
                                            <FormItem
                                              className="space-y-2 mb-3"
                                              data-error-anchor={
                                                insuranceField.name
                                              }
                                            >
                                              <FormLabel>
                                                Nurodykite draudimo įmonę
                                              </FormLabel>
                                              <Select
                                                value={
                                                  insuranceField.value || ''
                                                }
                                                onValueChange={
                                                  insuranceField.onChange
                                                }
                                                disabled={lockPaymentSection}
                                              >
                                                <FormControl>
                                                  <SelectTrigger className="w-full bg-white">
                                                    <SelectValue placeholder="Pasirinkite draudiką" />
                                                  </SelectTrigger>
                                                </FormControl>
                                                <SelectContent>
                                                  {insuranceOptions.map((c) => (
                                                    <SelectItem
                                                      key={c.value}
                                                      value={c.value}
                                                    >
                                                      {c.label}
                                                    </SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                              <FormMessage />
                                            </FormItem>
                                          )}
                                        />
                                      )}

                                      {opt.extra === 'corp' && (
                                        <FormField
                                          control={form.control}
                                          name="company"
                                          render={({ field: companyField }) => (
                                            <FormItem
                                              className="space-y-2 mb-3"
                                              data-error-anchor={
                                                companyField.name
                                              }
                                            >
                                              <FormLabel>
                                                Juridinio mokėtojo pavadinimas /
                                                kodas
                                              </FormLabel>
                                              <FormControl>
                                                <Input
                                                  className="bg-white"
                                                  value={
                                                    companyField.value || ''
                                                  }
                                                  onChange={(e) =>
                                                    companyField.onChange(
                                                      e.target.value,
                                                    )
                                                  }
                                                  placeholder="Įveskite įmonės pavadinimą ar kodą"
                                                  disabled={lockPaymentSection}
                                                />
                                              </FormControl>
                                              <FormMessage />
                                            </FormItem>
                                          )}
                                        />
                                      )}

                                      <div className="flex items-start gap-2 text-sm">
                                        <Info className="w-5 h-5 mt-0.5 shrink-0 text-primary" />
                                        <p className="leading-5">{opt.info}</p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </div>
                  </FormItem>
                )
              }
            />
          </section>

          {/* Sutikimas su taisyklėmis */}
          <section className="w-full space-y-3">
            <FormLabel>Sutikimas su taisyklėmis</FormLabel>

            <FormField
              control={form.control}
              name="agreePrivacy"
              render={({ field }) => (
                <FormItem
                  className="flex items-start gap-2 [&_[data-slot=label]]:leading-5"
                  data-error-anchor={field.name}
                >
                  <FormControl>
                    <Checkbox
                      id="agree-privacy"
                      className="mt-[2px]"
                      checked={!!field.value}
                      onCheckedChange={(v) => field.onChange(Boolean(v))}
                    />
                  </FormControl>
                  <div className="space-y-1">
                    <Label htmlFor="agree-privacy" layout="text">
                      Susipažinau ir sutinku su{' '}
                      <a
                        href={agreements.personalData.url}
                        className="underline"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        duomenų tvarkymo taisyklėmis
                      </a>
                      .
                    </Label>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="agreePortal"
              render={({ field }) => (
                <FormItem
                  className="flex items-start gap-2 [&_[data-slot=label]]:leading-5"
                  data-error-anchor={field.name}
                >
                  <FormControl>
                    <Checkbox
                      id="agree-portal"
                      className="mt-[2px]"
                      checked={!!field.value}
                      onCheckedChange={(v) => field.onChange(Boolean(v))}
                    />
                  </FormControl>
                  <div className="flex-1">
                    <Label htmlFor="agree-portal" layout="text">
                      Susipažinau ir sutinku su{' '}
                      <a
                        href={agreements.termsOfUse.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="underline"
                      >
                        išankstinės registracijos ir pacientų portalo
                        naudojimosi taisyklėmis
                      </a>
                      .
                    </Label>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Sutinku sumokėti visą užregistruotų paslaugų kainą, jeigu
                      registracijos laiku neatvyksiu į įstaigą gauti paslaugų
                      ir/ar neinformuosiu įstaigos apie neatvykimą bent prieš 24
                      val. iki numatyto paslaugų teikimo.
                    </p>
                    <FormMessage />
                  </div>
                </FormItem>
              )}
            />
          </section>
        </form>
      </Form>
    </FormProvider>
  );
}
